import React, { useState } from 'react';
import { Query } from 'react-apollo';
import { MagazineCard } from './MagazineCard';
import gql from 'graphql-tag';
import { Magazine } from '../server-types';
import { Spinner } from './Spinner';
import { nodes } from '../utils/data';
import { IFNTheme, registerClass } from '@food/css-manager';
import { cns, currySC } from '@food/css-manager/build/dist';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Select } from '@food/ui/build/dist';
import { magazineCardFragment } from '../utils/fragments';
import { DefaultLayout } from '../utils/layout';
import { RowFiller } from './ui/RowFiller';


interface MagazineCollectionHistoryProps {
	collectionId: string;
	category?: string;
	layout?: DefaultLayout | string;
	className?: string;
	sectionTitle?: boolean;
	yearSelection?: boolean;
}

const magazineCollectionMagazinesQuery = gql`
	query MagazineCollectionMagazinesQuery(
		$collectionId: ID!
		$after: DateTime
		$before: DateTime
		$category: String
	) {
		magazines(
			filter: {
				category: $category
				MagazineCollection: { id: $collectionId }
				isPublished: true
				publishedAfter: $after
				publishedBefore: $before
			}
		) {
			edges {
				node {
					...MagazineCardFragment
				}
			}
		}
	}
	${magazineCardFragment}
`;

const magazineSelectQuery = gql`
	query MagazineHistorySelectQuery($category: String, $collectionId: ID!) {
		first: magazines(
			filter: { category: $category, MagazineCollection: { id: $collectionId }, isPublished: true }
			first: 1
			orderBy: publicationDate
			orderDesc: false
		) {
			edges {
				node {
					id
					publicationDate
				}
			}
		}
		last: magazines(
			filter: { category: $category, MagazineCollection: { id: $collectionId }, isPublished: true }
			first: 1
			orderBy: publicationDate
			orderDesc: true
		) {
			edges {
				node {
					id
					publicationDate
				}
			}
		}
	}
`;

const styleClass = registerClass(
	(t: IFNTheme, sc) => `

	.${sc('icon')} {
		font-family: Material Icons
	}

	h2.${sc('section-title')} {
		margin-bottom: ${t.ratios.s * t.font.baseSize}px;
		font-size: ${t.ratios.xl}em;
		font-weight: ${t.font.weights.bold};
	}

	.${sc('filters')} {
		display: flex
		align-items: center;
		padding: ${t.ratios.s * t.font.baseSize * 0.9}px 0 ${t.ratios.s * t.font.baseSize}px;

		&.${sc('layout', DefaultLayout.MOBILE)} {
			flex-wrap: wrap;

			.${sc('filter')} {
				margin: auto;
			}
		}
	}

	.${sc('filter')} {
		margin-right: ${t.ratios.l * t.font.baseSize}px;
		border: 2px solid ${t.colors.alt.dark};
		border-radius: 0;
	}

	.${sc('search')} {
		flex: 0 1 100%;
		display: flex;
		align-items: flex-end;

		input {
			border: none;
			flex-grow: 1;
			border-bottom: 1px solid ${t.colors.grey.disabled.plain};
			color: ${t.colors.grey.dark};
			font-size: inherit;
			padding: ${(t.ratios.s / 2) * t.font.baseSize}px 0;
		}

		.${sc('icon')} {
			color: ${t.colors.grey.plain};
			font-size: ${t.ratios.l * 2}em;
			margin-left: ${t.ratios.xs * t.font.baseSize}px;
		}

		&.${sc('layout', DefaultLayout.MOBILE)} {
			flex-wrap: wrap;
			justify-content: center;

			input {
				margin-left: 0;
			}
		}
	}


	.${sc('list')} {
		display: flex;
		flex-wrap: wrap;
		justify-content: space-between;

		&.${sc('layout', DefaultLayout.MOBILE)} {
			padding: 0 calc(10% - 1rem);

			.${sc('card')} {
				max-width: calc(320px + 2rem);
				margin-left auto;
				margin-right: auto;
			}
		}
	}

	.${sc('card')} {
		margin-bottom: ${t.ratios.l * t.font.baseSize}px;
	}

	.${sc('fakeCard')} {
		width: 13vw;
		padding: 0 1vw 0;
		
		&.${sc('layout', DefaultLayout.TABLET)} {
			width: 24vw;
			padding: 0 3vw;
		}

		&.${sc('layout', DefaultLayout.MOBILE)} {
			width: 100%;
		}
	}

	`,
);

const sc = currySC(styleClass);

type FirstMagazineQueryResultType = {
	first: {
		edges: ReadonlyArray<{ node: Pick<Magazine, 'id' | 'publicationDate'> }>;
	};
	last: {
		edges: ReadonlyArray<{ node: Pick<Magazine, 'id' | 'publicationDate'> }>;
	};
};

export const MagazineCollectionHistory: React.FC<MagazineCollectionHistoryProps> = ({
	collectionId,
	category,
	layout = DefaultLayout.DESKTOP,
	className,
	sectionTitle = true,
	yearSelection = true
}) => {
	const { t } = useTranslation();
	const [selectedYear, setSelectedYear] = useState<string | null>(null);

	return (
		<Query<FirstMagazineQueryResultType, any>
			query={magazineSelectQuery}
			variables={{ category, collectionId }}
		>
			{({ error, loading, data }) => {
				if (error) {
					// TODO
					console.error(error);
					return null;
				}

				if (loading || !data) {
					return <Spinner />;
				}



				const firstYear = new Date(
					nodes(data.first)[0]?.publicationDate,
				).getFullYear();
				const lastYear = new Date(nodes(data.last)[0]?.publicationDate).getFullYear();

				const year = Number(selectedYear || lastYear);

				let after = dayjs(`${year}-01-01`).toString();
				let before = dayjs(`${year + 1}-01-01`).toString();

				const selectableYears: Array<{
					label: string;
					value: string;
				}> = Array.from(Array(lastYear - firstYear + 1), (_, i) =>
					(lastYear - i).toString(),
				).map((year) => ({ label: year, value: year }));

				if (!yearSelection) {
					after = dayjs(`1970-01-01`).toString();
					before = dayjs(`${lastYear}-12-31`).toString();
				}

				return (
					<div className={cns(styleClass, className)}>
						{sectionTitle && <h2 className={cns(sc('section-title'), sc('layout', layout))}>
							{t`see_previous_issues`}
						</h2>}
						{yearSelection && <div className={cns(sc('filters'), sc('layout', layout))}>
							<Select
								className={cns(sc('filter'), sc('layout', layout))}
								options={selectableYears}
								selected={year.toString()}
								onChange={(value: string) => setSelectedYear(value)}
							/>
						</div>}

						<Query<
							any,
							{
								after: string;
								before: string;
								collectionId: string;
								category?: string;
							}
						>
							query={magazineCollectionMagazinesQuery}
							variables={{
								collectionId,
								after,
								before,
								category
							}}
						>
							{({ error, loading, data }) => {
								if (error) {
									// TODO
									console.error(error);
									return null;
								}

								if (loading) {
									return <Spinner />;
								}

								const magazines = nodes(data.magazines);

								return (
									<div className={cns(sc('list'), sc('layout', layout))}>
										{magazines.length === 0 && (
											<div>
												Nessuna pubblicazione presente nell'anno selezionato
											</div>
										)}
										{magazines.length > 0 &&
											magazines.map((mag) => (
												<MagazineCard
													className={sc('card')}
													key={mag.id}
													magazine={mag}
													layout={layout}
												/>
											))}
										<RowFiller
											rowSize={11}
											arraySize={1}
											className={cns(
												sc('layout', layout),
												sc('fakeCard'),
											)}
										/>
									</div>
								);
							}}
						</Query>
					</div>
				);
			}}
		</Query>
	);
};
